// Global Instructions Rule Applied!

import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import type { RootState } from '../../../../state_manager/store.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { exportJsonToExcel } from '../../../../core/services/utilitiesService.ts'
import { buildReportingGridExcelPayload } from '../../../../state_manager/utils/reportingGridExcelExport.ts'
import { fetchAllCompaniesForExport } from '../../../shared/table_grid_export/fetchAllFilteredForExport.ts'

import CompaniesTable from './CompaniesTable.tsx'
import { useCompaniesData } from '../../hooks/useCompaniesData.ts'
import { useCompaniesDrawer } from '../../hooks/useCompaniesDrawer.ts'
import { useLookups } from '../../../global_configs/hooks/useLookups.ts'
import { useCurrencies } from '../../../currencies/useCurrencies.ts'
import { AccountRecordFields } from '../../../../state_manager/data_models/account_record.ts'
import { MENU_ITEM_KEY_COMPANIES, TABLE_ACCOUNTS } from '../../../../state_manager/globals.ts'

import ViewToolbar from '../../../../core/components/ViewToolbar/index.tsx'
import type { ViewConfigColumn } from '../../../../core/components/ViewConfig/types.ts'
import { ViewConfig } from '../../../../core/components/ViewConfig/index.ts'
import { useCompaniesReportId } from '../../../../state_manager/hooks/useCompaniesReportId.ts'
import { useReportingGridColumnsWithPreferences } from '../../../../state_manager/hooks/useReportingGridColumnsWithPreferences.ts'
import { getReportingGridColumnsForReportId } from '../../../../state_manager/utils/reportingGridColumns.ts'
import { getAddColumnsCatalogForReport } from '../../../../state_manager/utils/reportingColumnCatalog.ts'
import { augmentCompaniesViewConfigColumns } from '../../utils/companiesViewConfigColumnAugments.ts'
import { useMenuItemUserPreference } from '../../../global_configs/useMenuItemUserPreference.ts'

export default function CompaniesPage() {
  const { getLookupLabel, getLookupOptions } = useLookups()
  const { formatAmountById } = useCurrencies()

  const companiesReportId = useCompaniesReportId()
  const { preferenceRow: companiesPreferenceRowForFilterKeys } = useMenuItemUserPreference(MENU_ITEM_KEY_COMPANIES)
  const companiesGridColumnsForFilterKeys = useReportingGridColumnsWithPreferences(
    companiesReportId,
    companiesPreferenceRowForFilterKeys
  )
  const companiesFilterOptionKeys = useMemo(
    () =>
      companiesGridColumnsForFilterKeys
        .filter((c) => c.filterable !== false)
        .map((c) => String(c.filterKey || c.valueKey || ''))
        .filter((k) => k.length > 0),
    [companiesGridColumnsForFilterKeys]
  )

  const reportingConfigs = useSelector((state: RootState) => state.globalConfigs?.reporting_configs ?? [])
  const reportingColumnDefaults = useSelector(
    (state: RootState) => state.globalConfigs?.reporting_column_defaults ?? []
  )
  const reportingColumns = useSelector((state: RootState) => state.globalConfigs?.reporting_columns ?? [])

  const {
    companiesFilterOptions,
    companiesData,
    companiesDataLoading,
    companiesFilterOptionsLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault,
    handleFetchFilterOptions,
    handleFetchCompanies,
    handleSetFilters,
    handleSetSorting,
    defaultTableFilters,
    defaultTableSorting,
    handleResetFilters,
    handleLoadMore
  } = useCompaniesData(companiesFilterOptionKeys)

  const { handleCompanyNameClick, handleTasksClick, handleTaskToggle } = useCompaniesDrawer(companiesData)

  const companiesGridColumns = useReportingGridColumnsWithPreferences(companiesReportId, preferenceRow)

  const viewConfigColumns = useMemo(
    () => augmentCompaniesViewConfigColumns(companiesGridColumns),
    [companiesGridColumns]
  )

  const addColumnsCatalog = useMemo(
    () =>
      getAddColumnsCatalogForReport(
        companiesReportId,
        {
          reporting_columns: reportingColumns,
          reporting_column_defaults: reportingColumnDefaults
        },
        { primaryModuleKey: 'accounts' }
      ),
    [companiesReportId, reportingColumns, reportingColumnDefaults]
  )

  const catalogFieldNameOrder = useMemo(
    () => [...addColumnsCatalog.deal, ...addColumnsCatalog.related].map((c) => c.valueKey),
    [addColumnsCatalog]
  )

  const defaultTableColumnKeys = useMemo(() => {
    if (!companiesReportId) return null
    const rows = getReportingGridColumnsForReportId(companiesReportId, {
      reporting_configs: reportingConfigs,
      reporting_column_defaults: reportingColumnDefaults,
      reporting_columns: reportingColumns
    })
    return rows.map((r) => r.valueKey)
  }, [companiesReportId, reportingConfigs, reportingColumnDefaults, reportingColumns])

  const columnByValueKey = useMemo(() => {
    const m = new Map<string, ViewConfigColumn>()
    for (const c of addColumnsCatalog.deal) m.set(c.valueKey, c)
    for (const c of addColumnsCatalog.related) m.set(c.valueKey, c)
    for (const c of viewConfigColumns) {
      if (!m.has(c.valueKey)) m.set(c.valueKey, c)
    }
    return m
  }, [addColumnsCatalog, viewConfigColumns])

  const resolveViewConfigColumn = useCallback(
    (valueKey: string) => columnByValueKey.get(valueKey),
    [columnByValueKey]
  )

  const companiesColumnLayout = useMemo(
    () => ({
      onPersistFieldOrder: persistColumnFieldOrder,
      onResetToDefaults: resetColumnLayoutToDefault,
      resolveColumn: resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    }),
    [
      persistColumnFieldOrder,
      resetColumnLayoutToDefault,
      resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    ]
  )

  const cellEditErrors: Record<string, string> = {}

  const [exportLoading, setExportLoading] = useState(false)

  const handleExportTable = useCallback(async () => {
    setExportLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const { data, error } = await fetchAllCompaniesForExport(
        activeSorting,
        activeFilters,
        token,
        companiesReportId
      )
      if (error) {
        showErrorToast(error)
        return
      }
      if (data.length === 0) {
        toast.info('No rows to export')
        return
      }
      const records = data.map((r) => ({ ...r }) as Record<string, unknown>)
      const payload = buildReportingGridExcelPayload(companiesGridColumns, records)
      const filename = `Companies - ${dayjs().format('YYYY-MM-DD')}.xlsx`
      const result = await exportJsonToExcel({ ...payload, filename }, token)
      if (result.success) {
        toast.success(`${filename} exported successfully`)
      } else {
        showErrorToast(result.error || 'Export failed')
      }
    } finally {
      setExportLoading(false)
    }
  }, [activeSorting, activeFilters, companiesReportId, companiesGridColumns])

  return (
    <>
      <div className='h-full overflow-hidden'>
        <div className='w-full h-full flex flex-col bg-white px-2 sm:px-4 pb-2 sm:pb-4 overflow-hidden'>
          <ViewToolbar
            moduleKey={TABLE_ACCOUNTS}
            addButtonLabel='Company'
            searchPlaceholder='Search Companies...'
            recordTitleField={AccountRecordFields.accounts__name}
            activeFilters={activeFilters}
            currentFilterOptions={companiesFilterOptions}
            filterOptionsLoading={companiesFilterOptionsLoading}
            onRefreshFilterOptions={handleFetchFilterOptions}
            onRefreshData={() => handleFetchCompanies(activeSorting, activeFilters)}
            onFilterChange={handleSetFilters}
            defaultFilters={defaultTableFilters}
            onResetFilters={handleResetFilters}
            exportEnabled
            onExport={handleExportTable}
            exportLoading={exportLoading}
            beforeAddSlot={
              <ViewConfig
                moduleTitle='Companies'
                columns={viewConfigColumns}
                filterOptions={companiesFilterOptions}
                filterOptionsLoading={companiesFilterOptionsLoading}
                addColumnsCatalog={addColumnsCatalog}
                addColumnsTabLabels={{ primary: 'Company', related: 'Related' }}
                columnLayout={companiesColumnLayout}
                filteringConfig={{
                  currentFilters: activeFilters,
                  defaultFilters: defaultTableFilters,
                  onApplyFilters: async (filters) => {
                    handleSetFilters(filters)
                  },
                  onResetToDefaults: async () => {
                    handleSetFilters(defaultTableFilters)
                  }
                }}
                sortingConfig={{
                  currentSorting: activeSorting,
                  defaultSorting: defaultTableSorting,
                  onApplySorting: async (sorting) => {
                    handleSetSorting(sorting)
                  },
                  onResetToDefaults: async () => {
                    handleSetSorting(defaultTableSorting)
                  }
                }}
              />
            }
          />

          <div className='flex-1 min-h-0 h-full flex flex-col'>
            <CompaniesTable
              loading={companiesDataLoading}
              loadingMore={loadingMore}
              hasMore={hasMore}
              currentPage={currentPage}
              cellEditErrors={cellEditErrors}
              activeFilters={activeFilters}
              activeSorting={activeSorting}
              filterOptions={companiesFilterOptions}
              filterOptionsLoading={companiesFilterOptionsLoading}
              gridColumns={companiesGridColumns}
              onLoadMore={handleLoadMore}
              onFiltersChange={(filters) => {
                const filterArray = Object.entries(filters).map(([key, value]) => ({
                  key,
                  value
                }))
                handleSetFilters(filterArray)
              }}
              onSortChange={(sortConfig: any) => {
                if (sortConfig) {
                  handleSetSorting([{ key: sortConfig.column, direction: sortConfig.direction }])
                } else {
                  handleSetSorting([])
                }
              }}
              onCompanyNameClick={handleCompanyNameClick}
              onTasksClick={handleTasksClick}
              onTaskToggle={handleTaskToggle}
              getLookupLabel={getLookupLabel}
              getLookupOptions={getLookupOptions}
              formatAmountById={formatAmountById}
            />
          </div>
        </div>
      </div>
    </>
  )
}
